"use client";

import Image from "next/image";
import Link from "next/link";
import { CategoriesResponse } from "@/types/category";
import clsx from "clsx";
import SupportIcon from "@/assets/icons/support";
import OrdersIcon from "@/assets/icons/orders";
import StoreIcon from "@/assets/icons/store";
import CartNavIcon from "@/assets/icons/cart-nav";
import UserNavIcon from "@/assets/icons/user-nav";
import useUserStore from "@/global-store/user";
import { Typography } from "../typography";
import NavbarDropdown from "./navbar-dropdown";
import { MobileNavbar } from "./mobile-navbar";
import CartCount from "./cart-count";
import SearchBar from "./search-bar";

export const useUserRole = () => {
  const user = useUserStore((state) => state.user);
  const me = useUserStore((state) => state.me);
  if (!user) return null;
  return me?.role ?? user.role ?? null;
};

const navLinkClass =
  "px-2 gap-2 flex items-center text-gray-900 hover:text-primary transition-colors";

const CompanyLogo = ({ href }: { href: string }) => (
  <Link href={href} className="flex items-center">
    <Image
      src="/img/auth/logo.png"
      className=""
      height={56}
      width={142}
      alt="Logo"
    />
  </Link>
);
const CompanyLogoSmallerScreen = ({ href }: { href: string }) => (
  <Link href={href} className="flex items-center">
    <Image
      src="/img/Stroke.svg"
      className="m-5 "
      height={40}
      width={40}
      alt="Logo"
    />
  </Link>
);

export const ContactSupport = () => (
  <Link href="/contact-us" className={navLinkClass}>
    <SupportIcon size={23} className="shrink-0" />
    <Typography tag="span" className="text-nowrap">
      Support
    </Typography>
  </Link>
);

export const ContactOrders = () => {
  const user = useUserStore((state) => state.user);
  const role = useUserRole();
  if (!user || role === "companyAdmin") return null;

  return (
    <Link href="/orders" className={navLinkClass}>
      <OrdersIcon size={23} className="shrink-0" />
      <Typography tag="span" className="text-nowrap">
        Orders
      </Typography>
    </Link>
  );
};

export const ContactUser = () => (
  <Link href="/signin" className={navLinkClass}>
    <UserNavIcon size={23} className="shrink-0" />
    <Typography tag="span" className="text-nowrap">
      Account
    </Typography>
  </Link>
);

interface Props {
  categories: CategoriesResponse[];
}

const message = "Free Delivery on orders above $250 - Australia Wide*";

export default ({ categories }: Props) => {
  const user = useUserStore((state) => state.user);
  const role = useUserRole();

  return (
    <div className="sticky top-0 z-20 shadow-md h-fit">
      <div className="relative font-semibold bg-primary py-1.5 text-[#FDDB6C] overflow-hidden">
        <div className="whitespace-nowrap flex animate-marquee">
          <span className="mx-36">{message}</span>
          <span className="mx-36">{message}</span>
          <span className="mx-36">{message}</span>
          <span className="mx-36">{message}</span>
          <span className="mx-36">{message}</span>
          <span className="mx-36">{message}</span>
        </div>
      </div>

      <nav className="mx-auto bg-white ">
        <div className="flex flex-col max-w-screeen-2xl ">
          <div className="container max-w-screen-2xl mx-auto items-center justify-between ">
            {/* navbar for smaller scrren */}
            <div className="flex lg:hidden justify-between w-full  px-2">
              <div className="flex flex-row ">
                <MobileNavbar categories={categories} />
                <CompanyLogoSmallerScreen href={user ? "/products" : "/"} />
              </div>
              <div className="flex items-center p-2">
                <Link
                  href="/cart"
                  className="p-2 rounded flex items-center relative text-primary"
                >
                  <CartNavIcon size={35} className="shrink-0" />
                  <CartCount />
                </Link>
              </div>
            </div>

            {/* Navbar row 1 for large scren */}
            <div className="hidden container p-4 lg:flex items-center justify-between">
              <CompanyLogo href={user ? "/products" : "/"} />

              <div className="hidden lg:inline-block">
                <div className="relative  xl:w-[650px] lg:w-[400px] w-[280px]">
                  {!user && <SearchBar />}
                </div>
              </div>

              <div className="hidden gap-2  lg:flex">
                <div className="flex items-center container lg:flex-row flex-col gap-2">
                  <NavbarDropdown categories={categories} />
                  <Link href="/contact-us" className={navLinkClass}>
                    <SupportIcon size={23} className="shrink-0" />
                    <Typography tag="span" className="text-nowrap">
                      Support
                    </Typography>
                  </Link>
                  {user && (
                    <Link href="/orders" className={navLinkClass}>
                      <OrdersIcon size={23} className="shrink-0" />
                      <Typography tag="span" className="text-nowrap">
                        Orders
                      </Typography>
                    </Link>
                  )}
                </div>
                {user && role === "companyAdmin" && (
                  <Link href="/stores" className={navLinkClass}>
                    <StoreIcon />
                    <Typography tag="span" className="text-nowrap">
                      Stores
                    </Typography>
                  </Link>
                )}
                <Link
                  href="/cart"
                  className={clsx(
                    navLinkClass,
                    "hover:bg-[#F3F4F6] rounded-full p-1.5 relative",
                  )}
                >
                  <CartNavIcon className="shrink-0" />
                  <CartCount />
                </Link>
                <Link
                  href={user ? "/my-account" : "/signin"}
                  className={clsx(
                    navLinkClass,
                    "hover:bg-[#F3F4F6] rounded-full p-1.5",
                  )}
                >
                  <UserNavIcon size={23} className="shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
